import { usePathname } from 'next/navigation'
import { useEffect, useMemo, useState } from 'react'

import { EFilterKeys } from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import siteStateManager from '@src/quince-site-common/src/redux/globalStateManager/SiteStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { getSessionStorage } from '@src/utils/enhancedSessionStorage'
import { throttle } from '@src/utils/functions/utils'

import { useShouldHideDeptAndCategoryFilter, filterListOnExp } from '../../filterListOnExp'
import { Filter } from '../filterSection.interface'
import { excludeDepartmentFilter } from '../filterUtils'

const FILTER_KEY_GLOBAL = 'global'
const MOBILE_FILTER_DRAWER_STORAGE_KEY = 'mobile-collection-filter-drawer'
const MOBILE_FILTER_DRAWER_PATH_KEY = 'mobile-collection-filter-drawer-path'
const MOBILE_FILTER_COLLAPSE_STATE_KEY = 'mobile-collection-filter-collapse-state'

interface MobileFiltersProps {
  serverFilters: Filter[]
  collectionSlug?: string
  showGroupedPage?: boolean
}

interface MobileFiltersReturn {
  isDrawerOpen: boolean
  isSortDrawerOpen: boolean
  collapseActiveKeys: string[]
  headerHeight: number
  isScrollingDown: boolean
  filters: Filter[]
  openDrawerWithFilter: (filterName: string) => void
  openSortDrawer: () => void
  closeDrawer: () => void
  closeSortDrawer: () => void
  handleCollapseChange: (activeKeys: string[]) => void
  handleMainFilterClick: () => void
}

const useMobileFilters = ({
  serverFilters,
  collectionSlug,
  showGroupedPage = false,
}: MobileFiltersProps): MobileFiltersReturn => {
  // 1. Initialize state to a value that is consistent on both server and client
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [collapseActiveKeys, setCollapseActiveKeys] = useState<string[]>([])

  const [isSortDrawerOpen, setIsSortDrawerOpen] = useState(false)
  const [headerHeight, setHeaderHeight] = useState(0)
  const isScrollingDown = useAppSelector<boolean>(siteStateManager, siteStateManager.getIsScrollingDown)
  const pathname = usePathname()

  // 2. Use useEffect to read from sessionStorage AFTER the initial render (hydration).
  useEffect(() => {
    try {
      const storage = getSessionStorage()
      const storedDrawerState = storage.getItem<string>(MOBILE_FILTER_DRAWER_STORAGE_KEY, { isGlobal: true })
      const storedPath = storage.getItem<string>(MOBILE_FILTER_DRAWER_PATH_KEY, { isGlobal: true })

      // For grouped pages, only restore drawer state if the path matches exactly
      // This prevents the drawer from reopening after product type changes that cause page navigation
      if (storedDrawerState === 'true') {
        if (showGroupedPage && storedPath === pathname) {
          // For grouped pages, only restore if we're on the exact same path
          setIsDrawerOpen(true)
          const storedCollapseState = storage.getItem<string>(MOBILE_FILTER_COLLAPSE_STATE_KEY, { isGlobal: true })
          if (storedCollapseState) {
            setCollapseActiveKeys(JSON.parse(storedCollapseState))
          }
        } else if (!showGroupedPage) {
          // For non-grouped pages, restore as before
          setIsDrawerOpen(true)
          const storedCollapseState = storage.getItem<string>(MOBILE_FILTER_COLLAPSE_STATE_KEY, { isGlobal: true })
          if (storedCollapseState) {
            setCollapseActiveKeys(JSON.parse(storedCollapseState))
          }
        }
      }
    } catch (error) {
      console.error('Error reading initial filter state from session storage:', error)
    }
    // Include pathname in dependencies to trigger cleanup when path changes
  }, [pathname, showGroupedPage])

  // Get header height when component mounts and when scrolling direction changes
  useEffect(() => {
    if (isBrowser()) {
      const getHeaderHeight = throttle(() => {
        const headerElement = document.getElementById('site-primary-header')
        const subCollectionCardListElement = document.getElementById('subcollection-card-list')
        const subCategoryListElement = document.getElementById('sub-category-list-header')
        const headerHeight = headerElement?.getBoundingClientRect().height
        const subCollectionCardListHeight = subCollectionCardListElement?.getBoundingClientRect().height
        const subCategoryListHeight = subCategoryListElement?.getBoundingClientRect().height

        setHeaderHeight((headerHeight ?? 0) + (subCollectionCardListHeight ?? 0) + (subCategoryListHeight ?? 0))
      }, 50)

      getHeaderHeight()
      window.addEventListener('resize', getHeaderHeight, { passive: true })

      return () => {
        window.removeEventListener('resize', getHeaderHeight)
      }
    }
    return undefined
  }, [isScrollingDown])

  // Effect to persist drawer state to sessionStorage whenever it changes. This is correct.
  useEffect(() => {
    if (typeof window === 'undefined') return

    try {
      const storage = getSessionStorage()
      storage.setItem(MOBILE_FILTER_DRAWER_STORAGE_KEY, String(isDrawerOpen), { isGlobal: true })
      // Store the current path along with drawer state
      if (isDrawerOpen) {
        storage.setItem(MOBILE_FILTER_DRAWER_PATH_KEY, pathname, { isGlobal: true })
      }
    } catch (error) {
      console.error('Error storing filter drawer state:', error)
    }
  }, [isDrawerOpen, pathname])

  // Effect to persist collapse state. This is also correct.
  useEffect(() => {
    if (typeof window === 'undefined' || !isDrawerOpen) return

    try {
      const storage = getSessionStorage()
      storage.setItem(MOBILE_FILTER_COLLAPSE_STATE_KEY, JSON.stringify(collapseActiveKeys), { isGlobal: true })
    } catch (error) {
      console.error('Error storing collapse state:', error)
    }
  }, [collapseActiveKeys, isDrawerOpen])

  const hideDeptAndCategoryFilter = useShouldHideDeptAndCategoryFilter()
  const filters = useMemo(() => {
    const filteredList = filterListOnExp(
      excludeDepartmentFilter(serverFilters, collectionSlug),
      hideDeptAndCategoryFilter,
    ).filter(item => item.values.length > 1)

    return filteredList
  }, [serverFilters, hideDeptAndCategoryFilter, collectionSlug])

  // Enhanced drawer control with direct filter access
  const openDrawerWithFilter = (filterName: string) => {
    // Then open drawer with that filter expanded
    setIsDrawerOpen(true)
    setCollapseActiveKeys([filterName])
    const filterKey = filterName === EFilterKeys.SUBCOLLECTIONS ? EFilterKeys.PRODUCT_TYPE : filterName
    trackEvent(TELEMETRY_EVENT_NAMES.FILTER_OPENED, {
      filter_key: filterKey,
    })
  }

  // Reset handler for when the main filter button is clicked
  const handleMainFilterClick = () => {
    // Keep all filters collapsed by default
    setCollapseActiveKeys([])
    setIsDrawerOpen(true)
    trackEvent(TELEMETRY_EVENT_NAMES.FILTER_OPENED, {
      filter_key: FILTER_KEY_GLOBAL,
    })
  }

  const closeDrawer = () => {
    setIsDrawerOpen(false)
    setCollapseActiveKeys([])

    // Clear session storage when drawer is explicitly closed
    try {
      const storage = getSessionStorage()
      storage.removeItem(MOBILE_FILTER_DRAWER_STORAGE_KEY, { isGlobal: true })
      storage.removeItem(MOBILE_FILTER_DRAWER_PATH_KEY, { isGlobal: true })
      storage.removeItem(MOBILE_FILTER_COLLAPSE_STATE_KEY, { isGlobal: true })
    } catch (error) {
      console.error('Error clearing filter drawer state:', error)
    }
  }

  // Add these sort drawer functions
  const openSortDrawer = () => {
    setIsSortDrawerOpen(true)
    trackEvent(TELEMETRY_EVENT_NAMES.SORT_OPENED, {})
  }

  const closeSortDrawer = () => {
    setIsSortDrawerOpen(false)
  }

  // Add a collapseChange handler
  const handleCollapseChange = (activeKeys: string[]) => {
    const newlyOpenedFilters = activeKeys.filter(key => !collapseActiveKeys.includes(key))
    newlyOpenedFilters.forEach(filterKey => {
      const filterName = filterKey === EFilterKeys.SUBCOLLECTIONS ? EFilterKeys.PRODUCT_TYPE : filterKey
      trackEvent(TELEMETRY_EVENT_NAMES.FILTER_OPENED, {
        filter_key: filterName,
      })
    })

    setCollapseActiveKeys(activeKeys)
  }

  return {
    isDrawerOpen,
    isSortDrawerOpen,
    collapseActiveKeys,
    headerHeight,
    isScrollingDown,
    filters,
    handleCollapseChange,
    handleMainFilterClick,
    closeDrawer,
    openDrawerWithFilter,
    openSortDrawer,
    closeSortDrawer,
  }
}

export default useMobileFilters
